import { Info, type LucideIcon } from 'lucide-react';
import React from 'react';

import { SimpleTooltipV2 } from '@/components/ui/tooltip';
import { cn } from '@/utils';

interface InfoTooltipProps {
  /**
   * Tooltip body. Accepts any React node so callers can mix headings, lists,
   * spans with semantic color, etc. — not just plain text.
   */
  content: React.ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Visual size of the trigger icon. */
  iconClassName?: string;
  /** Override the trigger icon (defaults to the lucide `Info` glyph). */
  icon?: LucideIcon;
  /** Override the body's max width — default `max-w-xs` (~20rem) wraps comfortably. */
  contentClassName?: string;
}

/**
 * Standard info-icon tooltip used across the analytics surface.
 *
 * Uses the portal-based `SimpleTooltipV2` so:
 *   - It escapes `overflow:hidden` ancestors (cards, scroll areas).
 *   - Long copy wraps inside a width-capped body instead of clipping off
 *     the viewport edge.
 *
 * Pass the body as a React node — string is fine, but you can also pass
 * structured content (lists, multi-paragraph copy, styled spans).
 */
export const InfoTooltip: React.FC<InfoTooltipProps> = ({
  content,
  side = 'top',
  iconClassName,
  icon: Icon = Info,
  contentClassName,
}) => {
  if (!content) return null;
  return (
    <SimpleTooltipV2
      side={side}
      content={
        <div
          className={cn(
            'max-w-xs whitespace-normal break-words text-xs leading-relaxed',
            contentClassName,
          )}
        >
          {content}
        </div>
      }
    >
      <Icon
        className={cn('h-3.5 w-3.5 text-muted-foreground shrink-0', iconClassName)}
        aria-hidden
      />
    </SimpleTooltipV2>
  );
};
